import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ClipboardList, Pencil } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

const thisMonth = () => new Date().toISOString().slice(0, 7);

export const MmChecklist = () => {
  const { user } = useAuth();
  const isCEO = ["network_admin", "ceo"].includes(user.role);
  const [month] = useState(thisMonth());
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [notes, setNotes] = useState({});

  const load = useCallback(() => {
    api
      .get("/mm-targets", { params: { month } })
      .then(({ data }) => {
        setItems(data.items || []);
        setNotes(Object.fromEntries((data.items || []).map((i) => [i.id, i.note || ""])));
      })
      .catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const openEdit = () => {
    setText(items.map((i) => i.text).join("\n"));
    setEditing(true);
  };
  const saveList = async () => {
    try {
      const lines = text.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put("/mm-targets", { month, items: lines });
      toast.success("Targets published");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  const toggle = async (it, done) => {
    try {
      await api.patch(`/mm-targets/${month}/items/${it.id}`, { done });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  const saveNote = async (it) => {
    try {
      await api.patch(`/mm-targets/${month}/items/${it.id}`, { note: notes[it.id] || "" });
      toast.success("Note saved");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const done = items.filter((i) => i.done).length;

  return (
    <Card className="border-brand-line shadow-none" data-testid="mm-checklist">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <ClipboardList className="h-4 w-4 text-brand-pink" /> Marketing Manager targets
          {items.length > 0 && (
            <span className="text-sm font-normal text-slate-500">· {done}/{items.length} done</span>
          )}
        </CardTitle>
        {isCEO && !editing && (
          <Button size="sm" variant="outline" data-testid="mm-edit-btn" onClick={openEdit}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> {items.length ? "Edit list" : "Set targets"}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-2" data-testid="mm-editor">
            <p className="text-xs text-slate-500">One target per line.</p>
            <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} data-testid="mm-editor-textarea" />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveList} data-testid="mm-save-btn">Publish targets</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="mm-empty">No targets set for this month yet.</p>
        ) : (
          <ul className="space-y-3">
            {items.map((it) => (
              <li key={it.id} className="rounded-md border border-brand-line p-3" data-testid={`mm-item-${it.id}`}>
                <div className="flex items-start gap-2">
                  <Checkbox
                    checked={it.done}
                    disabled={isCEO}
                    onCheckedChange={(v) => toggle(it, !!v)}
                    data-testid={`mm-check-${it.id}`}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <p className={`text-sm ${it.done ? "text-slate-400 line-through" : "text-brand-ink"}`}>{it.text}</p>
                    {isCEO ? (
                      it.note ? (
                        <p className="mt-1 text-xs text-slate-600" data-testid={`mm-note-view-${it.id}`}>Note: {it.note}</p>
                      ) : (
                        <p className="mt-1 text-xs text-slate-400">No note yet</p>
                      )
                    ) : (
                      <div className="mt-2 flex gap-2">
                        <Input
                          className="h-8 text-xs"
                          placeholder="What did you do?"
                          value={notes[it.id] ?? ""}
                          data-testid={`mm-note-input-${it.id}`}
                          onChange={(e) => setNotes((n) => ({ ...n, [it.id]: e.target.value }))}
                        />
                        <Button size="sm" variant="outline" className="h-8" data-testid={`mm-note-save-${it.id}`} onClick={() => saveNote(it)}>
                          Save
                        </Button>
                      </div>
                    )}
                    {it.done && it.done_by && <p className="mt-1 text-[11px] text-emerald-600">Done by {it.done_by}</p>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
